import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { formatDistanceToNow, sub } from 'date-fns';
import { Grid, Row, Col, Panel } from 'react-bootstrap';
import { formatBytes } from '../../utils/formatUnits';
import { getPermissions } from '../../utils/getPermissions';
import UserProfile from '../UserProfile/UserProfile';
import {
  getLocalTime,
  getOnlineStatusStyle,
  getOnlineStatusText,
  getDownloadStatusText,
  downloadStatusToIconSrc,
} from './DeviceUtils';
import { classNames } from '../../utils/v4/utils';

export default class DeviceStatus extends Component {
  getThresholdColor = (type, value) => {
    const color = ['green', 'amber', 'red'];
    const threshold = {
      temp: [60, 80],
      disk: [60, 80],
      app_mem: [102400, 307200], // Player returns KBytes, thresholds: 100MB, 300MB
    };

    if (value === 0) {
      return 'unknown';
    }

    const active = threshold[type].find(item => item > value);
    const index = active ? threshold[type].indexOf(active) : 2;

    return ` color-${color[index]}-500`;
  };

  renderLocationCol() {
    const { device } = this.props;
    const { location_path } = device;

    return (
      <Col xs={4} sm={3} md={2} lg={2}>
        <Panel data-test="status-location">
          <Panel.Body>
            <span className="title !text-dark-night poppins-regular">Location</span>
            <span className="material-icons !text-dark-night">place</span>
            <span className="text text-dark-night poppins-regular">
              <span>{location_path === '/' ? 'No Location set' : location_path}</span>
            </span>
          </Panel.Body>
        </Panel>
      </Col>
    );
  }

  renderDeviceTypeCol() {
    const { device } = this.props;
    const { device_type = 'N/A', firmware_version } = device;

    return (
      <Col xs={4} sm={3} md={2} lg={2}>
        <Panel data-test="status-type">
          <Panel.Body>
            <span className="title !text-dark-night poppins-regular">Device Type</span>
            <span className="material-icons !text-dark-night">devices_other</span>
            <span className="text">
              <span>
                {device_type}
                <span className="comment">
                  {Boolean(firmware_version) && `fw: ${firmware_version}`}
                </span>
              </span>
            </span>
          </Panel.Body>
        </Panel>
      </Col>
    );
  }

  render() {
    const { auth, users, device } = this.props;
    const p = getPermissions(auth);

    if (!device) {
      return null;
    }

    const {
      serial = 'N/A',
      idle,
      uptime,
      modifiedBy,
      modifiedDate,
      updated,
      content_ver,
      temp,
      app_mem,
      ctrl_mem,
      disk,
      ip = 'N/A',
      mac,
      version = 'N/A',
    } = device;

    if (!p.atLeastTierProOrSuperUser) {
      return (
        <div className="device-status">
          <Grid className="device-status-icons" fluid>
            <Row>
              {this.renderLocationCol()}
              {this.renderDeviceTypeCol()}
            </Row>
          </Grid>
        </div>
      );
    }

    return (
      <div className="device-status">
        <Grid className="device-status-icons" fluid>
          <Row>
            {this.renderLocationCol()}
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-serial">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular">Serial Number</span>
                  <span className="material-icons !text-dark-night">fingerprint</span>
                  <span className="text">
                    <span>{serial}</span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-uptime">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular">Uptime</span>
                  <span className="material-icons !text-dark-night">power_settings_new</span>
                  <span className="text">
                    <span>
                      {Boolean(idle && uptime) &&
                        formatDistanceToNow(sub(new Date(), { seconds: uptime }))}
                    </span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-lastmodify">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular">Last Modify</span>
                  <UserProfile
                    avatarHolderClasses="flex items-center justify-center"
                    userId={modifiedBy}
                    users={users}
                  />
                  <span className="text">
                    <span>
                      <br />
                      {getLocalTime(modifiedDate)}
                      <span className="comment">
                        {formatDistanceToNow(new Date(modifiedDate))} ago
                      </span>
                    </span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-lastupdate">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular">Last Update</span>
                  <span className={`material-icons ${getOnlineStatusStyle(device)}`}>sync</span>
                  <span className="text">
                    <span>
                      {Boolean(idle) && getLocalTime(updated)}
                      <span className="comment">
                        {getOnlineStatusText(device).replace('Updated ', '')}
                      </span>
                    </span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-content">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular truncate">
                    Content Version
                  </span>
                  <div className="flex justify-center">
                    <img
                      data-test={`devices-table-up-to-date-status-${device.id}`}
                      src={downloadStatusToIconSrc(device)}
                      alt="Download status"
                      className={classNames(
                        'w-[25px] 2xl:w-[35px] 2xl:h-[35px]',
                        device.download_status === 'downloading' ? 'animate-pulse' : '',
                      )}
                    />
                  </div>

                  <span className="text">
                    <span>
                      {Boolean(idle) && getLocalTime(content_ver)}
                      <span className="comment">{getDownloadStatusText(device)}</span>
                    </span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-temperature">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular">Temperature</span>
                  <span
                    className={`fa fa-thermometer-half ${this.getThresholdColor('temp', temp)}`}
                  />
                  <span className="text">
                    <span>{temp ? `${temp} ℃` : 'N/A'}</span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-memory">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular">Memory</span>
                  <span className={`material-icons ${this.getThresholdColor('app_mem', app_mem)}`}>
                    memory
                  </span>
                  <span className="text">
                    <span>
                      {!app_mem && 'N/A'}
                      {Boolean(app_mem) && `${formatBytes(app_mem * 1024)} App`}
                      <br />
                      {Boolean(app_mem) && `${formatBytes(ctrl_mem * 1024)} Ctrl`}
                    </span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-storage">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular">Storage</span>
                  <span className={`material-icons ${this.getThresholdColor('disk', disk)}`}>
                    sd_storage
                  </span>
                  <span className="text">
                    <span>{disk ? `${disk}%` : 'N/A'}</span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-ip">
                <Panel.Body>
                  <span className="title !text-dark-night poppins-regular">IP/MAC</span>
                  <span className="material-icons !text-dark-night">developer_board</span>
                  <span className="text">
                    <span>
                      {ip}
                      <span className="comment">{Boolean(ip) && mac}</span>
                    </span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
            {this.renderDeviceTypeCol()}
            <Col xs={4} sm={3} md={2} lg={2}>
              <Panel data-test="status-version">
                <Panel.Body className="flex flex-col items-center">
                  <span className="title !text-dark-night poppins-regular">Player Version</span>
                  <img src="/assets/images/icons/logo_white_bg.png" alt="" />
                  <span className="text">
                    <span>{version}</span>
                  </span>
                </Panel.Body>
              </Panel>
            </Col>
          </Row>
        </Grid>
      </div>
    );
  }
}

DeviceStatus.propTypes = {
  auth: PropTypes.object,
  device: PropTypes.object,
  users: PropTypes.array,
};
